import {
  collection,
  CollectionReference,
  doc,
  DocumentReference,
  orderBy,
  query,
} from 'firebase/firestore';
import { collectionData, docData } from 'rxfire/firestore';
import { Observable, of } from 'rxjs';
import { map, mergeMap, switchMap, take } from 'rxjs/operators';
import dataKeys from '../../constants/sensor_data_keys';
import {
  ISensors,
  ISensorsShardStringified,
  ISensorsStringified,
  IUser,
} from '../types';
import { authenticatedUserProvider } from './userProvider';

const sensorsTest = (testId: string, user: IUser): Observable<ISensors> => {
  return docData(
    doc(
      user.services.firestore,
      `orgs/${user.orgId}/sensors/${testId}`
    ) as DocumentReference<ISensorsStringified>,
    { idField: 'id' }
  ).pipe(
    map(
      (sensorsTestStringified: ISensorsStringified): ISensors =>
        fromSensorsStringifiedToSensors(sensorsTestStringified)
    ),
    mergeMap((parent: ISensors): Observable<ISensors> => {
      if (!parent.sharded) {
        return of(parent);
      }
      return collectionData(
        query(
          collection(
            user.services.firestore,
            `orgs/${user.orgId}/sensors/${testId}/shards`
          ) as CollectionReference<ISensorsShardStringified>,
          orderBy('order', 'asc')
        )
      ).pipe(
        map((shards: ISensorsShardStringified[]): ISensors => {
          return mergeShards(parent, shards);
        })
      );
    })
  );
};

export const sensorsProvider = (testId: string): Observable<ISensors> => {
  return authenticatedUserProvider()
    .pipe(switchMap(sensorsTest.bind(null, testId)))
    .pipe(take(1));
};

/**
 * merges the parent document and its shards into a single document for processing
 * @param {Object} parent the parent sensors document
 * @param {Array<Object>} shards an array of sensors document shards
 * @returns {Object}
 */
const mergeShards = (
  parent: ISensors,
  shards: ISensorsShardStringified[]
): ISensors => {
  return shards.reduce(
    (acc: ISensors, curr: ISensorsShardStringified): ISensors => {
      dataKeys.forEach((key: string): void => {
        if (curr.hasOwnProperty(key)) {
          const currentData =
            typeof acc[key] === 'string' ? JSON.parse(acc[key]) : acc[key];
          acc[key] = [].concat(
            currentData,
            JSON.parse(ensureParseableArray(curr[key]))
          );
        }
      });
      return acc;
    },
    parent
  );
};

const fromSensorsStringifiedToSensors = (
  stringified: ISensorsStringified
): ISensors => {
  const leftBackLeft: number[] = JSON.parse(
    ensureParseableArray(stringified.leftBackLeft)
  );
  const leftBackRight: number[] = JSON.parse(
    ensureParseableArray(stringified.leftBackRight)
  );
  const leftFrontRight: number[] = JSON.parse(
    ensureParseableArray(stringified.leftFrontRight)
  );
  const leftFrontLeft: number[] = JSON.parse(
    ensureParseableArray(stringified.leftFrontLeft)
  );

  const rightBackLeft: number[] = JSON.parse(
    ensureParseableArray(stringified.rightBackLeft)
  );
  const rightBackRight: number[] = JSON.parse(
    ensureParseableArray(stringified.rightBackRight)
  );
  const rightFrontRight: number[] = JSON.parse(
    ensureParseableArray(stringified.rightFrontRight)
  );
  const rightFrontLeft: number[] = JSON.parse(
    ensureParseableArray(stringified.rightFrontLeft)
  );

  const trustrengthRaw: number[] = stringified.trustrengthRaw
    ? JSON.parse(ensureParseableArray(stringified.trustrengthRaw))
    : [];

  return {
    ...stringified,
    leftBackLeft,
    leftBackRight,
    leftFrontLeft,
    leftFrontRight,
    rightBackLeft,
    rightBackRight,
    rightFrontLeft,
    rightFrontRight,
    trustrengthRaw,
    trustrengthRatedOutput: stringified.trustrengthRatedOutput,
  };
};

const ensureParseableArray = (value: string): string => {
  const parseableArray = '[]';
  if (value === undefined || value === null) return parseableArray;
  if (value === 'NA') return parseableArray;
  return value;
};
